import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import classNames from 'classnames';

import { ModalHOC } from 'common/HOCs/ModalHOC';
import { NoItemsPanel } from 'common/components';
import { taggedStylesToString, withTheme } from 'common/styling/theme';

import { change } from 'redux-form';
// import { BasketIcon } from 'common/icons';
import FormFieldsComposer from '../FormFieldsComposer';

import { getDynamicStyles, staticStyles } from './style';

class MasterGenerateArrayField extends Component {
  // 深拷贝函数，不会序列化函数
  static deepClone(obj) {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }
    if (obj instanceof Date) {
      return new Date(obj.getTime());
    }
    if (obj instanceof Array) {
      return obj.map(item => MasterGenerateArrayField.deepClone(item));
    }
    if (typeof obj === 'object') {
      const clonedObj = {};
      Object.keys(obj).forEach(key => {
        clonedObj[key] = MasterGenerateArrayField.deepClone(obj[key]);
      });
      return clonedObj;
    }
  }

  // 动态创建包含condition字段的schema
  static createDynamicSchema(originalSchema) {
    if (!originalSchema || !originalSchema.items) {
      return originalSchema;
    }
    // 创建schema的深拷贝
    const dynamicSchema = MasterGenerateArrayField.deepClone(originalSchema);
    // 确保fields对象存在
    if (!dynamicSchema.items.properties) {
      dynamicSchema.items.properties = {};
    }

    // 设置name字段为只读
    if (dynamicSchema.items.properties.name) {
      dynamicSchema.items.properties.name.disabled = true;
    }
    return dynamicSchema;
  }

  // 保留所有字段，包括name字段（已设为只读）
  static filterSchemaFields(schema) {
    if (!schema || !schema.items || !schema.items.fields) return schema;
    // 创建schema的深拷贝
    const filteredSchema = MasterGenerateArrayField.deepClone(schema);
    // 保留所有字段，不再过滤name字段
    return filteredSchema;
  }

  static isValidGroup(group) {
    return (
      (typeof group === 'string' && group.trim().length > 0) || (typeof group === 'number' && Number.isFinite(group))
    );
  }

  static getGroupOptions(schema) {
    const options = schema?.groupOptions || schema?.group_options || [];
    return Array.isArray(options) ? options : [];
  }

  static getGroupName(group, groupOptions = []) {
    const option = groupOptions.find(item => String(item.value) === String(group));
    return option ? option.label : String(group);
  }

  static findRewardAmountItem(rewardAmount, group, groupName) {
    return (
      rewardAmount.find(
        item => item && (String(item.name) === String(group) || String(item.name) === String(groupName))
      ) || {}
    );
  }

  componentDidMount() {
    // 页面初始化时获取所有值
    // 检查当前fields是否已经匹配selectedGroups，避免不必要的重建导致数据清空
    const targetIndex = this.props.index !== undefined ? this.props.index : 0;
    const { fields, formData } = this.props;
    const currentFields = fields.getAll() || [];
    const selectedGroups = formData?.regular_account?.group_exceptions?.[targetIndex]?.value?.item || [];
    const groupOptions = MasterGenerateArrayField.getGroupOptions(this.props.schema);
    const currentNames = currentFields.map(f => f?.name).filter(Boolean);
    const selectedNames = selectedGroups.map(group => MasterGenerateArrayField.getGroupName(group, groupOptions));

    // 只有当fields为空或name列表与selectedGroups不匹配时才重新生成
    if (currentFields.length === 0 || JSON.stringify(currentNames) !== JSON.stringify(selectedNames)) {
      this.handleNextButtonClick();
    }
  }

  componentDidUpdate(prevProps) {
    // 获取当前和之前的formData
    const currentFormData = this.props.formData;
    const prevFormData = prevProps.formData;
    const targetIndex = this.props.index !== undefined ? this.props.index : 0;

    // 当外层group_exceptions数组长度变化（添加/删除组）时，
    // 只有当本组的关键数据确实变化了才重新生成，避免其它组数据被清空
    const currentExceptions = currentFormData?.regular_account?.group_exceptions || [];
    const prevExceptions = prevFormData?.regular_account?.group_exceptions || [];

    // 如果本组的索引超出了数组范围，说明本组被删除了，不需要处理
    if (targetIndex >= currentExceptions.length) {
      return;
    }

    // 比较关键数据是否变化，避免无限循环
    const currentSelectedGroups = currentExceptions[targetIndex]?.value?.item || [];
    const prevSelectedGroups = prevExceptions[targetIndex]?.value?.item || [];
    // 只比较reward_amount的name列表，不比较内部reward数据的变化
    // 避免内嵌reward ArrayField的Add/Remove操作触发整体重新生成
    const currentRewardAmount = currentExceptions[targetIndex]?.value?.reward_amount || [];
    const prevRewardAmount = prevExceptions[targetIndex]?.value?.reward_amount || [];
    const currentRewardNames = currentRewardAmount.map(item => item?.name).filter(Boolean);
    const prevRewardNames = prevRewardAmount.map(item => item?.name).filter(Boolean);
    const groupOptionsChanged =
      JSON.stringify(MasterGenerateArrayField.getGroupOptions(this.props.schema)) !==
      JSON.stringify(MasterGenerateArrayField.getGroupOptions(prevProps.schema));

    // 只有当选中的groups或reward_amount的name列表变化时才重新初始化
    const groupsChanged = JSON.stringify(currentSelectedGroups) !== JSON.stringify(prevSelectedGroups);
    const rewardNamesChanged = JSON.stringify(currentRewardNames) !== JSON.stringify(prevRewardNames);

    if (groupsChanged || rewardNamesChanged || groupOptionsChanged) {
      this.handleNextButtonClick();
    }
  }

  onDragEnd = result => {
    const { fields } = this.props;

    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const startIndex = result.source.index;
    const endIndex = result.destination.index;
    const [removed] = fields.getAll().splice(startIndex, 1);

    fields.splice(endIndex, 0, removed);
  };

  // Next按钮
  handleNextButtonClick = index => {
    // 如果传入了index参数，使用传入的值；否则使用props中的index（如果有的话），如果都没有则使用默认值0
    const targetIndex = index !== undefined ? index : this.props.index !== undefined ? this.props.index : 0;
    const { fields, formData, dispatch, formName } = this.props;
    console.log('in-------------------------------');
    // 尝试多种可能的路径获取选中的group items
    let selectedGroups = [];
    // let conditions = [];

    // 主路径：从regular_account.group_exceptions获取
    if (formData?.regular_account?.group_exceptions && formData.regular_account.group_exceptions.length > 0) {
      const selectedException = formData.regular_account.group_exceptions[targetIndex];
      selectedGroups = selectedException?.value?.item || [];
    }
    const rewardAmount = formData?.regular_account?.group_exceptions?.[targetIndex]?.value?.reward_amount;
    const rewardAmountItems = Array.isArray(rewardAmount) ? rewardAmount : [];
    const groupOptions = MasterGenerateArrayField.getGroupOptions(this.props.schema);

    // 清理与spread/percentage类型无关的残留字段，避免出现 value.value 之类的嵌套数据
    const currentType = formData?.regular_account?.group_exceptions?.[targetIndex]?.type;
    if (currentType && (currentType === 'percentage' || currentType === 'spread')) {
      const originalValue = formData?.regular_account?.group_exceptions?.[targetIndex]?.value || {};
      // 确保只保留 item 和 reward_amount 字段
      const cleanedValue = {
        item: originalValue.item || [],
        reward_amount: originalValue.reward_amount || [],
      };
      // 检查是否需要清理
      const hasExtraFields = Object.keys(originalValue).some(key => !['item', 'reward_amount'].includes(key));
      if (hasExtraFields) {
        dispatch(change(formName, `regular_account.group_exceptions[${targetIndex}].value`, cleanedValue));
        // 同步更新本地 selectedGroups 使用清洗后的数据
        selectedGroups = cleanedValue.item;
      }
    }

    // 清空现有的所有字段，重新生成数据
    const currentFields = fields.getAll() || [];
    // 从后往前删除，避免索引变化问题
    for (let i = currentFields.length - 1; i >= 0; i -= 1) {
      fields.remove(i);
    }
    // 为每个选中的group重新创建奖励条目
    selectedGroups.forEach(group => {
      if (MasterGenerateArrayField.isValidGroup(group)) {
        const groupName = MasterGenerateArrayField.getGroupName(group, groupOptions);
        const existingItem = MasterGenerateArrayField.findRewardAmountItem(rewardAmountItems, group, groupName);
        let rewardData;

        if (existingItem && Array.isArray(existingItem.reward) && existingItem.reward.length > 0) {
          // 完整保留现有的reward数据
          rewardData = existingItem.reward;
        } else {
          // 从已有数据中读取：兼容旧数据从顶层获取
          const existingValue = existingItem.value;
          const rewardItem = {
            value: existingValue !== undefined ? existingValue : null,
          };
          rewardData = [rewardItem];
        }

        const newItem = {
          name: groupName, // 设置name为展示名称，不保存Group ID
          reward: rewardData, // 完整保留reward数据
        };

        fields.push(newItem);
      }
    });
  };

  render() {
    const {
      schema,
      fields,
      meta: { error, pristine },
      formName,
      arraysName,
      submitting,
      theme,
    } = this.props;

    const style = taggedStylesToString(staticStyles);
    const dynamicStyles = Object.keys(theme).length ? taggedStylesToString(getDynamicStyles(theme)) : ` `;

    const formError = !pristine && error;
    const isDragDisabled = !schema.ordering || fields.length === 1;
    const isFormError = typeof error === 'object';

    // 创建动态schema，包含condition字段
    const dynamicSchema = MasterGenerateArrayField.createDynamicSchema(schema);

    // 过滤schema，移除name字段
    const filteredSchema = MasterGenerateArrayField.filterSchemaFields(dynamicSchema);
    return (
      <div className="FieldArray" id={arraysName}>
        <div className="FieldArray__top-line">
          <div className="FieldArray__title">
            <FormattedMessage id={schema.title}>{txt => <h2>{txt}</h2>}</FormattedMessage>
          </div>
        </div>

        {fields.length ? (
          <DragDropContext onDragEnd={this.onDragEnd}>
            <Droppable droppableId="droppable" isDropDisabled={isDragDisabled}>
              {(provided, snapshot) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className={classNames('FieldArray__isDragging-inner', { isDraggingOver: snapshot.isDraggingOver })}>
                  {fields.map((item, index) => {
                    // 获取当前item的name作为唯一key
                    const itemName = fields.getAll()[index]?.name || `item_${index}`;
                    return (
                      <Draggable key={itemName} draggableId={item} index={index} isDragDisabled={isDragDisabled}>
                        {(provided, snapshot) => (
                          <div
                            className={classNames('FieldArray__item-inner', {
                              FieldArray__notDraggable: !schema.ordering,
                              FieldArray__isDraggable: snapshot.isDragging,
                              FieldArray__item_error: isFormError && error[index],
                            })}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={provided.draggableProps.style}>
                            <div className="FieldArray__item">
                              <p className="FieldArray__item-number">{index + 1}</p>
                              <FormFieldsComposer
                                name={`${item}`}
                                schema={filteredSchema.items}
                                formName={formName}
                                submitting={submitting}
                              />
                              {isFormError && error[index] && (
                                <FormattedMessage id={error[index]}>
                                  {txt => <span className="ArrayField__item-error">{txt}</span>}
                                </FormattedMessage>
                              )}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          <NoItemsPanel
            // captionSlug={schema.title}
            // descriptionSlug={schema.description}
            className="FieldArray__no-items"
            isError={!!formError}
          />
        )}

        <style>{style}</style>
        <style>{dynamicStyles}</style>
      </div>
    );
  }
}

MasterGenerateArrayField.propTypes = {
  schema: PropTypes.object.isRequired,
  formName: PropTypes.string.isRequired,
  showModal: PropTypes.func.isRequired,
  meta: PropTypes.object.isRequired,
  fields: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  arraysName: PropTypes.string.isRequired,
  submitting: PropTypes.bool,
  theme: PropTypes.object,
  formData: PropTypes.object,
  index: PropTypes.number,
};

MasterGenerateArrayField.defaultProps = {
  fields: {},
  submitting: false,
  theme: {},
  formData: {},
  index: 0,
};

// 使用connect从Redux store获取表单数据
const mapStateToProps = (state, ownProps) => {
  const formData = state.form && state.form[ownProps.formName] ? state.form[ownProps.formName].values || {} : {};
  return {
    formData,
  };
};

const ThemedArrayField = compose(connect(mapStateToProps), withTheme(), ModalHOC)(MasterGenerateArrayField);

export default ThemedArrayField;
export { MasterGenerateArrayField };
